import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import type { PopperProps } from '@mui/material';
import debounce from 'lodash/debounce';
import inventoryItemService from '../../services/inventoryItemService';
import type { InventoryItemWithBalance } from '../../services/inventoryItemService';
import { sizeCaption } from '../../utils/inventoryItemDisplay';

/**
 * Popper that grows to fit the widest option but never shrinks below the input.
 * Autocomplete sets `style.width` to the anchor width (a number); we keep that
 * as the min and let content push the panel wider so long wine names stay on
 * one readable line. (REPORTING-CENTER.2.1)
 */
const GrowToFitPopper = (props: PopperProps) => {
  const anchorWidth = typeof props.style?.width === 'number' ? props.style.width : undefined;
  return (
    <Popper
      {...props}
      placement="bottom-start"
      style={{ ...props.style, width: 'fit-content' }}
      sx={{ minWidth: anchorWidth ?? 260, maxWidth: 560 }}
    />
  );
};

/**
 * MENU-EXEC.4 — reusable inventory-item picker.
 *
 * A controlled Autocomplete over inventoryItemService.getItems. Extracted from
 * the search pattern previously inlined in FormatLinkPanel / InventoryRecipeEditor
 * so the item-level fulfillment builder (and others) can reuse it.
 *
 * Read-only/search-only: it never mutates anything — selection is reported via
 * onChange and the parent owns the linkage write.
 */

interface InventoryItemSearchBoxProps {
  venueId: string;
  value: InventoryItemWithBalance | null;
  onChange: (item: InventoryItemWithBalance | null) => void;
  /**
   * RECIPE-TYPE.R7 — show the stock-vs-recipe chip on each option.
   *
   * The list endpoint always returns recipe items; there was never a server-side
   * include flag (the old query parameter was silently ignored). This flag only
   * ever controlled the chip, so it is now named for what it actually does.
   */
  showExecutionBehavior?: boolean;
  label?: string;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
}

const inventoryItemLabel = (item: InventoryItemWithBalance): string => {
  const name = item.displayName;
  const size = item.sizeValue != null ? `${item.sizeValue} ${item.sizeUnit ?? 'ml'}` : null;
  return size ? `${name} · ${size}` : name;
};

export default function InventoryItemSearchBox({
  venueId,
  value,
  onChange,
  showExecutionBehavior = false,
  label = 'Inventory item',
  helperText,
  placeholder = 'Search inventory…',
  disabled = false,
}: InventoryItemSearchBoxProps) {
  const [searchResults, setSearchResults] = useState<InventoryItemWithBalance[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const runSearch = useMemo(
    () =>
      debounce(async (q: string) => {
        if (!q.trim()) {
          setSearchResults([]);
          setSearchLoading(false);
          return;
        }
        setSearchLoading(true);
        try {
          const res = await inventoryItemService.getItems(
            venueId, 1, 20, 'displayName', 'asc', q,
          );
          setSearchResults(res.data ?? []);
        } catch {
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      }, 350),
    [venueId],
  );
  useEffect(() => () => runSearch.cancel(), [runSearch]);

  // Keep the controlled value selectable even before/without a matching search
  // result, so MUI doesn't warn about a value outside the options list.
  const options = useMemo(() => {
    if (value && !searchResults.some(o => o._id === value._id)) {
      return [value, ...searchResults];
    }
    return searchResults;
  }, [value, searchResults]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={value}
      loading={searchLoading}
      disabled={disabled}
      filterOptions={(x) => x}
      getOptionLabel={inventoryItemLabel}
      isOptionEqualToValue={(opt, val) => opt._id === val._id}
      slots={{ popper: GrowToFitPopper }}
      onInputChange={(_, v, reason) => {
        if (reason === 'input') runSearch(v);
        if (reason === 'clear') onChange(null);
      }}
      onChange={(_, val) => onChange(val)}
      renderOption={(props, opt) => {
        // The name is the canonical displayName; size shows as a right-aligned
        // caption only when the name doesn't already end with it (WINE names do).
        const size = sizeCaption(opt.displayName, opt.sizeValue, opt.sizeUnit);
        return (
          <Box
            component="li"
            {...props}
            key={opt._id}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
          >
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }} title={opt.displayName}>
              {opt.displayName}
            </Typography>
            {size && (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {size}
              </Typography>
            )}
            {/* The stock-vs-recipe chip only clarifies anything when recipe items
                are in the list; for plain stock pickers it's noise, so hide it. */}
            {showExecutionBehavior && (
              <Chip
                size="small"
                label={opt.executionBehavior === 'RECIPE_CONSUMPTION' ? 'Made from a recipe' : 'Sold from stock'}
                color={opt.executionBehavior === 'RECIPE_CONSUMPTION' ? 'secondary' : 'info'}
                variant="outlined"
                sx={{ flexShrink: 0 }}
              />
            )}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {searchLoading && <CircularProgress size={14} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
